import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { AuthTokenService } from '@kempoweroyj/ngx-auth-token-service';
import {
    ArchiveNotificationsRequest,
    ArchiveNotificationsResponse,
    ArchiveStatus,
    ExternalNotificationsApiServiceObservable,
    FrontendNotification,
    GetNotificationResponse,
    GetNotificationsQueryParameters,
    GetNotificationsResponse,
    GetNotificationStatsResponse,
    MarkReadNotificationsRequest,
    MarkReadNotificationsResponse,
    NotificationCounts,
} from '@kempoweroyj/ngx-external-notifications-api-client';
import {
    CensoredRowWithTenancyLocationRow,
    GetLocationsResponse,
    LocationsApiServiceObservable,
} from '@kempoweroyj/ngx-locations-api-client';
import { catchError, combineLatest, map, Observable, ObservableInput, of, startWith, Subject } from 'rxjs';

export const notificationStatuses = ['inbox', 'archived', 'all'] as const;
export type EnrichedFrontendNotification = FrontendNotification & {
    siteName: string;
    vehicleNames: Array<string>;
};
export type GetNotificationsWithSiteNamesResponse = {
    status: string;
    data: Array<EnrichedFrontendNotification>;
    wasLastPage: boolean;
};
export type GetSingleNotificationResponse = {
    status: string;
    notification: undefined | EnrichedFrontendNotification;
};
export type GetNotificationEmailSentTimestampResponse = {
    status: string;
    timestamp: undefined | string;
};

export const fetchedNotifications: WritableSignal<Array<EnrichedFrontendNotification>> = signal([]);
const readStatusSubject = new Subject<MarkReadNotificationsResponse>();
const archiveStatusSubject = new Subject<ArchiveNotificationsResponse>();
const DEFAULT_NOTIFICATION_LIMIT = 50;

@Injectable()
export class NotificationDataService {
    readonly #notificationsApiServiceObservable = inject(ExternalNotificationsApiServiceObservable);
    readonly #authTokenService = inject(AuthTokenService);
    readonly #locationsService = inject(LocationsApiServiceObservable);

    #siteMapCache: Map<string, string> = new Map();
    #siteMapCacheTime = 0;

    lastPaginationToken = '';
    headers = {
        headers: {
            Authorization: 'Bearer ' + this.#authTokenService.jwt,
        },
    };

    clearFetchedNotifications(): void {
        fetchedNotifications.set([]);
    }

    readStatusSubject$(): Observable<MarkReadNotificationsResponse> {
        return readStatusSubject.asObservable();
    }

    archiveStatusSubject$(): Observable<ArchiveNotificationsResponse> {
        return archiveStatusSubject.asObservable();
    }

    getSitesNamesMappedById$(): Observable<Map<string, string>> {
        const now = Date.now();
        const siteMapCacheDurationMillis = 60000; // cache for 1 minute
        if (now - this.#siteMapCacheTime < siteMapCacheDurationMillis) {
            return of(this.#siteMapCache);
        }
        return this.#locationsService.getLocations().pipe(
            map((response: GetLocationsResponse) => {
                const siteMap = new Map();
                response.locations.forEach((site: CensoredRowWithTenancyLocationRow) => {
                    siteMap.set(site.id, site.name as string);
                });
                this.#siteMapCache = siteMap;
                this.#siteMapCacheTime = now;
                return siteMap;
            })
        );
    }

    getNotifications$(type: ArchiveStatus, clearPaginationToken?: boolean): Observable<Array<FrontendNotification>> {
        if (clearPaginationToken) {
            this.lastPaginationToken = '';
        }
        const params: GetNotificationsQueryParameters = {
            archiveStatus: type,
            limit: DEFAULT_NOTIFICATION_LIMIT,
            paginationToken: this.lastPaginationToken,
        };

        return this.#notificationsApiServiceObservable.getNotifications(params, this.headers).pipe(
            map((notifications: GetNotificationsResponse) => {
                this.lastPaginationToken = notifications.paginationToken;
                return notifications.notifications;
            })
        );
    }

    getNotificationsWithSiteNames$(type: ArchiveStatus, clearPaginationToken?: boolean): Observable<GetNotificationsWithSiteNamesResponse> {
        return combineLatest([this.getSitesNamesMappedById$(), this.getNotifications$(type, clearPaginationToken)]).pipe(
            map(([locationsMap, notifications]) => {
                const notificationsList: Array<EnrichedFrontendNotification> = this.#enrichNotificationData({
                    notifications: notifications,
                    locationsMap: locationsMap,
                });

                if (clearPaginationToken) {
                    this.clearFetchedNotifications();
                }

                fetchedNotifications.set(fetchedNotifications().concat(notificationsList));
                return {
                    status: 'success',
                    data: notificationsList,
                    wasLastPage: notifications.length < DEFAULT_NOTIFICATION_LIMIT,
                };
            }),
            startWith({ status: 'loading', data: [], wasLastPage: false })
        );
    }

    getSingleNotification$(notificationId: string): Observable<GetSingleNotificationResponse> {
        return combineLatest([this.getSitesNamesMappedById$(), this.#notificationsApiServiceObservable.getNotification(notificationId, this.headers)]).pipe(
            map(([locationsMap, notification]) => {
                const notificationsList: Array<EnrichedFrontendNotification> = this.#enrichNotificationData({
                    notifications: [notification],
                    locationsMap: locationsMap,
                });

                return {
                    status: 'success',
                    notification: notificationsList[0],
                };
            }),
            startWith({ status: 'loading', notification: undefined })
        );
    }

    getNotificationEmailSentTimestamp$(notificationId: string): Observable<GetNotificationEmailSentTimestampResponse> {
        return this.#notificationsApiServiceObservable.getNotification(notificationId, this.headers).pipe(
            map((notification: GetNotificationResponse) => {
                return {
                    status: 'success',
                    timestamp: notification.deliveryStatus?.email?.timestamp,
                };
            }),
            startWith({ status: 'loading', timestamp: undefined })
        );
    }

    getUnreadCounts$(): Observable<NotificationCounts> {
        return this.#notificationsApiServiceObservable.getNotificationStats(this.headers).pipe(
            map((res: GetNotificationStatsResponse) => {
                return res.count;
            })
        );
    }

    updateNotificationReadStatus(params: { markAll: boolean; notificationIds: Array<string>; read: boolean; archiveStatusFilter: ArchiveStatus }): void {
        const request: MarkReadNotificationsRequest = {
            markAll: params.markAll,
            notificationIds: params.notificationIds,
            readStatus: params.read ? 'read' : 'sent',
            archiveStatusFilter: params.archiveStatusFilter,
        };

        this.#notificationsApiServiceObservable
            .markReadNotifications(request, this.headers)
            .pipe(
                catchError((): ObservableInput<MarkReadNotificationsResponse> => {
                    return of({
                        notificationIds: params.markAll ? ['', ''] : [''],
                        readStatus: request.readStatus,
                        success: false,
                    });
                })
            )
            .subscribe((response: MarkReadNotificationsResponse): void => {
                // Update also 'fetchedNotifications' so there won't be data mismatch
                fetchedNotifications().forEach((notification) => {
                    if (response.notificationIds.includes(notification.id)) {
                        notification.status = response.readStatus;
                    }
                });
                readStatusSubject.next(response);
            });
    }

    updateNotificationArchivedStatus(params: { markAll: boolean; notificationIds: Array<string>; archiveStatus: boolean }): void {
        const request: ArchiveNotificationsRequest = {
            markAll: params.markAll,
            notificationIds: params.notificationIds,
            archiveStatus: params.archiveStatus,
        };

        this.#notificationsApiServiceObservable
            .archiveNotifications(request, this.headers)
            .pipe(
                catchError((): ObservableInput<ArchiveNotificationsResponse> => {
                    return of({
                        notificationIds: params.markAll ? ['', ''] : [''],
                        archiveStatus: params.archiveStatus,
                        success: false,
                    });
                })
            )
            .subscribe((response: ArchiveNotificationsResponse): void => {
                // Update also 'fetchedNotifications' so there won't be data mismatch
                fetchedNotifications().forEach((notification) => {
                    if (response.notificationIds.includes(notification.id)) {
                        notification.archived = response.archiveStatus;
                    }
                });
                archiveStatusSubject.next(response);
            });
    }

    static getAvailableNotificationStatusUrls() {
        return notificationStatuses;
    }

    /**
     * Enriches notifications' data by adding field: "siteName".
     * If siteName is not found, use id as a fallback.
     **/
    #enrichNotificationData(params: { notifications: Array<FrontendNotification>; locationsMap: Map<string, string> }): Array<EnrichedFrontendNotification> {
        const notificationsList: Array<EnrichedFrontendNotification> = [];

        params.notifications.forEach((notification: FrontendNotification) => {
            let siteName: string;
            if (params.locationsMap.has(notification.events?.[0]?.scope?.['locationId'])) {
                siteName = params.locationsMap.get(notification.events?.[0]?.scope?.['locationId']) as string;
            } else {
                siteName = notification.events?.[0]?.scope?.['locationId'];
            }

            notificationsList.push({
                ...notification,
                siteName: siteName,
                vehicleNames: [], // Vehicle names are gathered in single notification's component
            });
        });

        return notificationsList;
    }
}
